import type { ReactNode } from 'react'

/**
 * La bibliothèque d'icônes de la console — TRACÉS EXTRAITS TELS QUELS du
 * `const ICONS` d'origine, par script, pour qu'aucun ne soit redessiné.
 *
 * ⚠️ Ce n'est pas un détail d'inventaire. Chaque icône que j'avais dessinée
 * moi-même avait ses propres dimensions et sa propre épaisseur de trait, au
 * lieu de la règle partagée `.ic` : `width:1em` (donc à la taille du texte
 * voisin), `stroke:currentColor` (donc de la couleur héritée), `fill:none`,
 * `stroke-width:2`. Résultat visible : le petit nuage de la barre météo ne
 * ressemblait à rien, et aucune icône ne suivait la taille de son texte.
 *
 * La seule transformation appliquée aux tracés est la syntaxe des attributs
 * (`stroke-width` → `strokeWidth`), imposée par JSX.
 */
const TRACES: Record<string, ReactNode> = {
  sun: (
    <><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>
  ),
  cloud: (
    <><path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6 1.5A3.75 3.75 0 0 0 6.5 19z" /></>
  ),
  rain: (
    <><path d="M17.5 15a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6 1.5A3.75 3.75 0 0 0 6.5 15" /><path d="M8 18l-1 3M12 18l-1 3M16 18l-1 3" /></>
  ),
  storm: (
    <><path d="M17.5 13a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6 1.5A3.75 3.75 0 0 0 6.5 13" /><path d="M12 12l-2 4h3l-2 4" /></>
  ),
  book: (
    <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>
  ),
  scale: (
    <><path d="M12 3v18M6 21h12" /><path d="M6 7l-3 6h6zM18 7l-3 6h6z" /><path d="M6 7l6-2 6 2" /></>
  ),
  chart: (
    <><path d="M3 3v18h18" /><path d="M7 14l3-4 3 3 4-6" /></>
  ),
  receipt: (
    <><path d="M5 2v20l2.2-1.4L9.4 22l2.2-1.4L13.8 22l2.2-1.4L18.2 22l1.8-1.2V2l-1.8 1.2L16 1.8 13.8 3.2 11.6 1.8 9.4 3.2 7.2 1.8z" /><path d="M8 8h8M8 12h8M8 16h5" /></>
  ),
  download: (
    <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></>
  ),
  wallet: (
    <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></>
  ),
  shield: (
    <><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></>
  ),
  users: (
    <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>
  ),
  plus: (
    <><path d="M12 5v14M5 12h14" /></>
  ),
  check: (
    <><path d="M20 6L9 17l-5-5" /></>
  ),
  edit: (
    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></>
  ),
  x: (
    <><path d="M18 6L6 18M6 6l12 12" /></>
  ),
  star: (
    <><path className="ic-fill" d="M12 2l3 6.9 7.5.6-5.7 4.9 1.8 7.3L12 17.8 5.6 21.7l1.8-7.3L1.7 9.5 9.2 8.9z" /></>
  ),

  /* ── Sous-onglets de la Comptabilité ──────────────────────────────────
     Ces neuf-là n'étaient pas dans le `const ICONS` d'origine : elles sont
     écrites en SVG dans le balisage même des boutons `#acc-tabs`. C'est ce qui
     les a fait manquer au portage — la barre comptable était la seule sans
     pictogrammes. Extraites par script depuis ce balisage, comme les autres.

     ⚠️ `balance` et `courbe` RESSEMBLENT à `scale` et `chart` sans leur être
     identiques (plateaux et sommets tracés autrement). Les fondre au prétexte
     du doublon changerait le dessin de la barre. En revanche « Journal » et
     « Saisies » reprennent exactement `book` et `edit` : eux sont réutilisés. */
  grille: (
    <><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></>
  ),
  balance: (
    <><path d="M12 3v18" /><path d="M7 21h10" /><path d="M5 7h14l-3 6H8z" /><path d="M5 7 3 12M19 7l2 5" /></>
  ),
  courbe: (
    <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></>
  ),
  bilan: (
    <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 3v18M3 9h18" /></>
  ),
  flux: (
    <><path d="M7 10l5-6 5 6" /><path d="M17 14l-5 6-5-6" /><path d="M12 4v16" /></>
  ),
  reglages: (
    <><path d="M20 7h-9M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" /></>
  ),
  plan: (
    <><path d="M4 4h16v4H4zM4 12h16v8H4zM8 8v4M16 8v4" /></>
  ),
}

export type NomIcone = keyof typeof TRACES

/**
 * `viewBox` et `className` viennent de `ic()` : c'est la classe `.ic` qui
 * porte toute la mise en forme. Un `<svg>` sans elle se dessine en noir plein,
 * à 300 px de côté — la taille par défaut d'un SVG sans dimensions.
 */
export function Icone({ nom }: { nom: string }) {
  return (
    <svg className="ic" viewBox="0 0 24 24">
      {TRACES[nom] ?? null}
    </svg>
  )
}

/**
 * Condition météo (en français, telle que saisie dans Config) → icône.
 * Repris de `WICON` : le rapprochement se fait sur le libellé parce que les
 * barèmes sont écrits à la main par le dispatch.
 */
export function iconeMeteo(condition: string): string {
  const table: Record<string, string> = {
    ensoleille: 'sun', ensoleillé: 'sun', degage: 'sun',
    nuageux: 'cloud', couvert: 'cloud', brumeux: 'cloud',
    pluie: 'rain', pluvieux: 'rain', pluvieuse: 'rain',
    orage: 'storm', orageux: 'storm',
  }
  return table[(condition || '').toLowerCase()] ?? 'sun'
}
